import React, { useCallback, useMemo } from 'react'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Box, Button, CircularProgress, Typography, useMediaQuery } from '@material-ui/core'
import { useApi } from '~/store/store'
import { mutualTheme } from '~/utils/theme'
import { CloseIcon, MailIcon } from '~/assets/icons'
import { HfNumberMaskedInput, HfTextInput } from '~/components'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { CashedDataPayload, AccountPendenciesCachedData, MODAL_TYPE } from '../..'

export interface UpdateInfoModalProps {
  email?: string,
  phone?: string,
  isEmail?: boolean,
  toggleModal: () => void,
  cachedData: AccountPendenciesCachedData,
  setOpenedModalType: (modalType: MODAL_TYPE) => void,
  modifyCachedData: (key: keyof AccountPendenciesCachedData, payload: CashedDataPayload) => void
}

export type FormValues = {
  email: string,
  phone: string
}

const FORM_DEFAULT_VALUES = {
  email: '',
  phone: ''
}

const validationSchema = yup.object().shape({
  email: yup.string()
    .matches(/^[a-z0-9._{|}-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/, 'Email inválido')
    .email('Email inválido'),
  phone: yup.string().min(15, 'Número de telefone inválido')
})

export const UpdateInfoModal: React.FC<UpdateInfoModalProps> = (props) => {
  const { isEmail, cachedData, modifyCachedData, setOpenedModalType } = props
  const [updatePhone, updatePhoneState] = useApi('updatePhone')
  const [updateEmail, updateEmailState] = useApi('updateEmail')
  const { phone, email } = cachedData
  const formatedChannel = isEmail ? 'email' : 'telefone'
  const { _id: accountId } = useAuthAccount()

  const isLoading = useMemo(() => updatePhoneState.loading || updateEmailState.loading, [updatePhoneState.loading, updateEmailState.loading])

  const matches = useMediaQuery(mutualTheme.breakpoints.down('md'))

  const { handleSubmit, control, formState: { errors } } = useForm<FormValues>({
    defaultValues: isEmail ? (email ? { email, phone } : FORM_DEFAULT_VALUES) : (phone ? { email, phone } : FORM_DEFAULT_VALUES),
    resolver: yupResolver(validationSchema)
  })

  const updateCurrentChannel = useCallback(async (formData: FormValues) => {
    const { email, phone } = formData

    if (isEmail) {
      return await updateEmail({ accountId, email })
    }

    return await updatePhone({ accountId, phone })
  }, [updatePhone, updateEmail, isEmail, accountId])

  const onSubmit: SubmitHandler<FormValues> = async (formData) => {
    const { phone, email } = formData

    const response = await updateCurrentChannel(formData)

    if (response.ok) {
      modifyCachedData(isEmail ? 'email' : 'phone', isEmail ? email : phone)
      setOpenedModalType(isEmail ? MODAL_TYPE.ValidateEmailModal : MODAL_TYPE.ValidatePhoneModal)
      return
    }

    toast.error(`Erro ao atualizar ${formatedChannel}`, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
      closeButton: false
    })
  }

  const onCancelClick = () => {
    setOpenedModalType(isEmail ? MODAL_TYPE.ValidateEmailModal : MODAL_TYPE.ValidatePhoneModal)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: matches ? 280 : 640 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center', marginBottom: 4 }}>
          <Typography sx={{ fontWeight: 'fontWeightBold' }} variant={matches ? 'airbnbH3' : 'airbnbH2'}>
            {isEmail ? 'Alterar e-mail' : 'Alterar telefone'}
          </Typography>
          <Typography variant={'airbnbBody1'}>
            Atualize os seus dados abaixo:
          </Typography>
        </Box>
        <Box>
          {
            isEmail && (
              <HfTextInput
                label='Email'
                outsideLabel
                control={control}
                name='email'
                errorMessage={errors.email?.message}
                error={errors.email?.message}
                placeholder='financeiro@minhaempresa.com.br'
              />
            )
          }
          {
            !isEmail && (
              <HfNumberMaskedInput
                name='phone'
                label='Telefone'
                formatted
                outsideLabel
                mask='(##) #####-####'
                control={control}
                error={errors.phone?.message}
                placeholder='(00) 00000-0000'
              />
            )
          }
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: [
            'column-reverse',
            'column-reverse',
            'row',
            'row',
            'row'
          ],
          justifyContent: 'space-between',
          marginTop: 3
        }}>
          <Button
            variant='contained'
            color='info'
            size='large'
            onClick={onCancelClick}
            startIcon={<CloseIcon htmlColor={mutualTheme.palette.secondary.main} />}
            sx={{
              marginTop: [2, 2, 0, 0, 0],
              height: 56,
              fontFamily: 'Airbnb',
              fontWeight: 'fontWeightBold',
              color: 'secondary.main',
              ':hover': {
                bgcolor: 'rgba(54,141,247,0.2)'
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            size='large'
            type='submit'
            color='primary'
            variant='contained'
            disabled={isLoading}
            startIcon={isLoading
              ? <CircularProgress color='inherit' size={20} />
              : <MailIcon htmlColor={mutualTheme.palette.common.white} />
            }
            sx={{
              height: 56,
              fontFamily: 'Airbnb',
              fontWeight: 'fontWeightBold'
            }}
          >
            Salvar
          </Button>
        </Box>
      </Box >
    </form>
  )
}
